import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';

import { map, catchError } from 'rxjs/operators';
import { Observable, throwError } from 'rxjs';

import { SentryLogger, ConfigService } from '@fcom/core';
import { CheckboxOptionList, createQueryString } from '@fcom/service-forms';

import {
  BaggageTracingBag,
  BaggageTracingItenerary,
  BaggageTracingSubmissionData,
  BaggageTracingAPISubmissionData,
} from '../interfaces';
import { SubmitService } from '../services/submit.service';

@Injectable()
export class BaggageTracingService {
  constructor(
    private submitService: SubmitService,
    private http: HttpClient,
    private sentryLogger: SentryLogger,
    private configService: ConfigService
  ) {}

  /**
   * Return values:
   * 1 = Email sending is successfull
   * 0 = Something went wrong with sending email
   * -1 = Attatchment virus scan failed
   * All of them are returned with success 200
   * @param query
   */
  public baggageTracing(query): Observable<any> {
    const submitQuery = this.valuesForBaggageTracingApi(query);

    const apiUrl = this.configService.cfg.serviceForm.portalServices.apiUrl;
    const keyId = this.configService.cfg.serviceForm.portalServices.keyId;

    const httpOptions = {
      headers: this.submitService.buildHeaders({ 'x-api-key': keyId }),
    };
    const request = `${apiUrl}/mailer?${createQueryString(submitQuery)}`;

    return this.http.get<any>(request, httpOptions).pipe(
      map((response) => {
        if (response.returnValue === '1') {
          return response;
        }
        throw new Error('Error submitting Baggage tracing form request');
      }),
      // eslint-disable-next-line rxjs/no-implicit-any-catch
      catchError((err) => {
        this.sentryLogger.warn(err.message, { request, error: err });
        return throwError(() => err);
      })
    );
  }

  public get bagOptionsControls(): CheckboxOptionList {
    return [
      { id: 'Zipper', label: 'SERVICE_FORMS.NTServiceForms.fields.bagOptions.zipper' },
      { id: 'Lock', label: 'SERVICE_FORMS.NTServiceForms.fields.bagOptions.lock' },
      { id: 'Wheels', label: 'SERVICE_FORMS.NTServiceForms.fields.bagOptions.wheels' },
      { id: 'Retractable handle', label: 'SERVICE_FORMS.NTServiceForms.fields.bagOptions.retractableHandle' },
      { id: 'Combination lock', label: 'SERVICE_FORMS.NTServiceForms.fields.bagOptions.combinationLock' },
    ];
  }

  /**
   * Return key value pairs from formArray checkbox list,
   * for selected options based on provided mapping obj
   * @param formArrayValue
   * @param optionsMap
   */
  public parseFormArrayOptions(formArrayValue: Array<boolean>, optionsMap: CheckboxOptionList): object {
    return formArrayValue
      .map((value, index) => {
        return {
          [optionsMap[index].id]: value ? optionsMap[index].id : null,
        };
      })
      .reduce((rest, curr) => ({ ...rest, ...curr }));
  }

  private valuesForBaggageTracingApi(formValue: BaggageTracingSubmissionData): BaggageTracingAPISubmissionData {
    const bagOptions = this.parseFormArrayOptions(formValue.bagOptions, this.bagOptionsControls);
    const iteneraries = this.transformItenerary(formValue.itenerary);
    const bagItems = this.transformItems(formValue.bagItems);

    return {
      '01___Reference_number': formValue.baggageReferenceNumber || 'not filled',
      '02___First_name': formValue.firstName,
      '03___Last_name': formValue.lastName,
      '04___Home_address': formValue.homeAddress,
      '05___Email': formValue.email,
      '06___Phone_number': `${formValue.phonePrefix.replace('|', ' ')}${formValue.phoneNumber}`,
      '07___Mobile_number': `${formValue.phonePrefix.replace('|', ' ')}${formValue.phoneNumber}`,
      '08___Temporary_address': this.valueOrNull(formValue.temporaryAddress),
      '09___Temporary_address_valid_from': formValue.temporaryAddressValidFrom
        ? formValue.temporaryAddressValidFrom.toDottedUIString()
        : null,
      '10___Temporary_address_valid_to': formValue.temporaryAddressValidTo
        ? formValue.temporaryAddressValidTo.toDottedUIString()
        : null,
      '11___Plus_number': this.valueOrNull(formValue.cardNumber),
      '12___Finnair_plus_tier': this.valueOrNull(formValue.finnairPlusTier),
      '14___Baggage_last_seen': this.valueOrNull(formValue.baggageLastSeen),
      '15___Number_of_pieces_checked': this.valueOrNull(formValue.numberOfPiecesChecked),
      '16___Baggage_Identification_Tag_numbers': this.valueOrNull(formValue.baggageIdentificationTagNumbers),
      '17___When_and_where_loss_reported': this.valueOrNull(formValue.whenAndWhereLossReported),
      '18___Baggage_insurance_policy_number': this.valueOrNull(formValue.baggageInsurancePolicyNumber),
      '19___Insurance_company_name_and_address': this.valueOrNull(formValue.insuranceCompanyNameAndAddress),
      '20___Anything_particular_at_check_in': this.valueOrNull(formValue.anythingParticularAtCheckIn),
      '21___Colour': this.valueOrNull(formValue.color),
      '22___Brand': this.valueOrNull(formValue.brand),
      '23___Type_of_bag': this.valueOrNull(formValue.typeOfBag),
      '24___Bag_was': this.valueOrNull(formValue.bagWasHard),
      '25___Bag_had_zipper': bagOptions['Zipper'],
      '26___Bag_had_lock': bagOptions['Lock'],
      '27___Bag_had_combination_lock': bagOptions['Combination lock'],
      '28___Lock_number_combination': this.valueOrNull(formValue.combinationLockCode),
      '30___Bag_had_wheels': bagOptions['Wheels'],
      '32___Bag_had_retractable_handle': bagOptions['Retractable handle'],
      '33___Baggage_name_tag_description': this.valueOrNull(formValue.baggageNameTagDescription),
      '34___Baggage_identifiable_features': this.valueOrNull(formValue.baggageIdentifiableFeatures),
      '50___Items_to_identify_baggage': this.valueOrNull(formValue.baggageIdentifiableFeatures),
      __addressKey: 'emailServlet.baggageTracing',
      __messageSubject: 'Baggage tracing form',
      __confirmationTo: formValue.email,
      __emailType: 'BaggageTracing',
      ...iteneraries,
      ...bagItems,
    };
  }

  private valueOrNull(val) {
    return val || null;
  }

  /**
   * Return dynamically created formArray bag items
   * in badly named format requested by API
   * 35___Item_2_quantity
   * @param items
   */
  private transformItems(items: BaggageTracingBag[]): object {
    if (items.length === 0) {
      return {};
    }
    return items
      .map((bagItem, index) => {
        const itemNumber = 35 + index;
        const quantity = `${itemNumber}___Item_${index + 1}_quantity`;
        const name = `${itemNumber}___Item_${index + 1}`;
        const description = `${itemNumber}___Item_${index + 1}_description`;
        const belongsTo = `${itemNumber}___Item_${index + 1}_belongs_to`;

        return {
          [name]: bagItem.itemName,
          [quantity]: bagItem.itemQuantity,
          [belongsTo]: bagItem.itemBelongsTo,
          [description]: bagItem.itemDescription,
        };
      })
      .reduce((rest, curr) => ({ ...rest, ...curr }));
  }

  /**
   * Return dynamically created formArray iteneraries
   * in badle named format requested by API
   * 13___Flight_1_form
   * @param flights
   */
  private transformItenerary(flights: BaggageTracingItenerary[]): object {
    if (flights.length === 0) {
      return {};
    }
    return flights
      .map((flight, index) => {
        const from = `13___Flight_${index + 1}_from`;
        const destination = `13___Flight_${index + 1}_to`;
        const flightNumber = `13___Flight_${index + 1}`;
        const date = `13___Flight_${index + 1}_date`;

        return {
          [from]: flight.origin,
          [destination]: flight.destination,
          [flightNumber]: flight.flightNumber,
          [date]: flight.flightDate.toDottedUIString(),
        };
      })
      .reduce((rest, curr) => ({ ...rest, ...curr }));
  }
}
